/**
 * Error helper for the wsr-api operational surface (WSRPDM-0012).
 *
 * `src/api/client.ts` always routes a non-2xx response through
 * `apiErrorFromEnvelope()` (`../errors.ts`), which assumes the saas-auth
 * `{success:false,error:{code,message}}` envelope. wsr-api's error body is
 * flat instead — `{code,message,status,field?}` (contract §0.6) — so that
 * parse finds no nested `.error` and `ApiError.code` normalizes to
 * `'UNKNOWN'`. The raw parsed body still survives in `ApiError.details.raw`
 * (it stores whatever `JSON.parse` produced) — `wsrApiError()` reads it back
 * as the real wsr-api shape without touching the shared envelope parser.
 */
import { ApiError } from '../errors';
import type { WsrApiErrorBody } from './types';

/**
 * Extract the raw wsr-api error body from an `ApiError` thrown by
 * `request()`. Returns `null` for non-`ApiError` throws, network errors, and
 * bodyless 401/403s (Spring Security defaults — contract §0.6).
 */
export function wsrApiError(err: unknown): WsrApiErrorBody | null {
  if (!(err instanceof ApiError)) return null;
  const raw = err.details.raw;
  if (raw && typeof raw === 'object' && 'code' in raw && 'message' in raw) {
    return raw as WsrApiErrorBody;
  }
  return null;
}
